import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { BiData, TopNavClickBiEvent } from '@wix/da-bi/pkg/events';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import Headerless from '@wix/da-ds/pkg/modals/dsPresets/HeaderlessModal/HeaderlessModal';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';

const X_RESET_TIME = 1000;

export interface Props {
  isLoggedIn: boolean;
  className?: string;
  menuClassName?: string;
}

const MoreMenu: React.FC<React.PropsWithChildren<Props>> = ({
  isLoggedIn,
  className,
  menuClassName,
}) => {
  const { t } = useTranslation();
  const [showMuro, setShowMuro] = useState(false);
  const isForumsSisuFlagEnabled = useFlag('forums_sisu');
  const { MiniComponent } = useContext(MuroDepContext);

  const [closeRequested, setCloseRequested] = useState(false);
  const doClose = useCallback(() => {
    setShowMuro(false);
  }, [setShowMuro]);
  const handleClose = useCallback(() => {
    setCloseRequested(true);

    // This timeout is to "reset" the x button, so if the close gets cancelled the
    // user can try to close again later
    setTimeout(() => {
      setCloseRequested(false);
    }, X_RESET_TIME);
  }, [setCloseRequested]);

  const items: NullableDropdownItem[] = useMemo(
    () => [
      {
        items: [
          {
            key: 'dreamup',
            label: t('siteHeader.dreamup'),
            link: Url.dreamupLink(),
            target: isLoggedIn ? undefined : '_blank',
            biData: BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'dreamup',
              link_url: Url.dreamupLink(),
            }),
          },
          {
            key: 'muro',
            label: 'Draw',
            onClick: () => {
              if (isLoggedIn) {
                if (MiniComponent) {
                  setShowMuro(true);
                } else {
                  // This is just a fallback, in case there is a page that we forgot
                  // to wrap in the muro context. Prefer the modal if we can do it.
                  window.location.assign(
                    Url.studio({
                      playground: 'draw',
                      openSubmitForm: true,
                    })
                  );
                }
              } else {
                window.open(Url.joinLink(), '_self');
              }
            },
            biData: BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'draw',
              link_url: Url.studio({ playground: 'draw' }),
            }),
          },
          {
            key: 'video',
            label: t('siteHeader.video'),
            link: Url.videoCreatorLink(),
            target: isLoggedIn ? undefined : '_blank',
            newTag: 'purple',
            biData: BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'video',
              link_url: Url.videoCreatorLink(),
            }),
          },
          {
            key: 'create-group',
            label: t('siteHeader.createGroup'),
            link: Url.to(URLS.GROUPS_CREATE),
            biData: BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'create-group',
              link_url: Url.to(URLS.GROUPS_CREATE),
            }),
          },
          {
            key: 'monetize',
            label: t('siteHeader.monetize'),
            link: URLS.SHOP_TOP_NAV,
            target: '_blank',
            biData: BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'deviantartsell',
              link_url: URLS.SHOP_TOP_NAV,
            }),
          },
        ],
      },
      {
        items: [
          {
            key: 'forum',
            label: t('siteHeader.forum'),
            link:
              isForumsSisuFlagEnabled && !isLoggedIn
                ? Url.joinLink('site-header-link-forums', Url.buildFromPath(URLS.FORUM))
                : Url.buildFromPath(URLS.FORUM),
            biData: BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'forum',
              link_url: Url.buildFromPath(URLS.FORUM),
            }),
          },
          {
            key: 'whatsNew',
            label: t('siteHeader.whatsNew'),
            link: Url.to(URLS.TEAM),
            target: '_blank',
            biData: BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'whatsNew',
              link_url: Url.to(URLS.TEAM),
            }),
          },
          {
            key: 'communityEvents',
            label: t('siteHeader.communityEvents'),
            link: 'https://www.deviantart.com/moonbeam13/journal/Community-Events-1155532870',
            target: '_blank',
            biData: BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'communityEvents',
              link_url: Url.to(URLS.TEAM),
            }),
          },
          {
            key: 'helpCenter',
            label: t('siteHeader.userMenu.helpAndFAQ'),
            link: URLS.HELP,
            target: '_blank',
            biData: BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'help_center',
              link_url: URLS.HELP,
            }),
          },
        ],
      },
    ],
    [t, isLoggedIn, MiniComponent]
  );

  return (
    <>
      <ErrorBoundary debugComponent="MoreMenu">
        <IconButtonWithContextMenu
          className={className}
          standardMenuProps={{ className: menuClassName }}
          popperManagerProps={{ isInPopup: true }}
          placement="bottom-end"
          openOnHover
          items={items}
          size="large"
          variant="main"
          icon={EllipsisIcon}
          aria-label={t('siteHeader.more')}
        />
      </ErrorBoundary>

      {showMuro && MiniComponent && (
        <Headerless useFixedMobileHeight isOpen={true} hasCloseButton onRequestClose={handleClose}>
          <MiniComponent
            canvasSize={1152}
            aspectRatio={'4:3'}
            onRequestClose={doClose}
            closeRequested={closeRequested}
          />
        </Headerless>
      )}
    </>
  );
};
MoreMenu.displayName = 'MoreMenu';

export default MoreMenu;
